import { GoogleGenerativeAI, HarmCategory, HarmBlockThreshold } from '@google/generative-ai';
import { ModerationContext } from './moderation';
import { analyzeMessageSequence } from './contextRules';

const genAI = new GoogleGenerativeAI(import.meta.env.VITE_GEMINI_API_KEY);

// Transportation-specific system prompt (for admin chat interface)
const TRANSPORTATION_SYSTEM_PROMPT = `You are a context-aware content moderation AI. Analyze text based on the following contexts:

1. TRIP STAGES:
- before_pickup: Monitor for scams, verify identity
- during_ride: Heightened awareness for safety threats
- after_dropoff: Strict monitoring of personal boundaries

2. RELATIONSHIP CONTEXT:
- Driver-Passenger: Professional service relationship
- Power Dynamics: Driver has responsibility for passenger safety
- Boundaries: Strict professional conduct requirements

3. CONVERSATION PATTERNS:
- Previous Messages: Consider full conversation context
- Escalation: Monitor for increasing hostility/inappropriate behavior
- Professional Boundaries: Enforce service-appropriate communication
- Defensive Responses: Recognize legitimate responses to inappropriate behavior

Guidelines for classification:
- 'neutral': Safe, appropriate content with no issues or concerns
- 'spam': Promotional, repetitive, or unsolicited commercial content
- 'hate_speech': Content expressing hatred or prejudice against protected groups
- 'threat': Direct or implied threats of violence or harm
- 'personal_attack': Direct insults, name-calling, or character attacks
- 'harassment': Targeted, persistent negative behavior or bullying
- 'explicit_content': Adult, NSFW, or inappropriate sexual content
- 'misinformation': False or misleading claims and information
- 'self_harm': Content promoting or discussing self-injury
- 'violence': Graphic violence or glorification of violent acts
- 'emotional_manipulation': Content designed to manipulate emotions or gaslight
- 'profanity': Strong language, swearing, or vulgar content
- 'hate_group': Content promoting extremist groups or ideologies
- 'conspiracy': Unfounded conspiracy theories or disinformation
- 'impersonation': Content pretending to be from another person/entity
- 'trolling': Intentionally provocative or disruptive content
- 'doxxing': Sharing private or personal information without consent
- 'copyright': Unauthorized use of copyrighted material
- 'phishing': Scam attempts or fraudulent content
- 'extremism': Content promoting extreme ideological views

Additional Severity Modifiers:
- After-dropoff personal requests: +50% severity
- During-ride threats: +100% severity
- Driver-initiated personal comments: +50% severity
- Sequential boundary violations: +75% severity

Respond with ONLY this JSON structure:
{
  "flags": [
    {
      "type": "neutral" | "spam" | "hate_speech" | "threat" | "personal_attack" | "harassment" | "explicit_content" | "misinformation" | "self_harm" | "violence" | "emotional_manipulation" | "profanity" | "hate_group" | "conspiracy" | "impersonation" | "trolling" | "doxxing" | "copyright" | "phishing" | "extremism",
      "reason": "Detailed explanation of why this classification was chosen",
      "confidence": 0.95,
      "context": "Relevant text excerpt",
      "severity": "low" | "medium" | "high"
    }
  ],
  "overallToxicity": 0.75,
  "summary": {
    "spamScore": 0.2,
    "toxicityScore": 0.75,
    "profanityCount": 1,
    "emotionalIntensity": 0.6,
    "threatLevel": 0.3,
    "manipulationScore": 0.4,
    "credibilityScore": 0.8
  }
}`;

// General system prompt (for demo and chrome extension)
const GENERAL_SYSTEM_PROMPT = `You are a content moderation AI designed to identify harmful or inappropriate content. Use these classification guidelines:

Guidelines for classification:
- 'neutral': Safe, appropriate content with no issues or concerns
- 'spam': Promotional, repetitive, or unsolicited commercial content
- 'hate_speech': Content expressing hatred or prejudice against protected groups
- 'threat': Direct or implied threats of violence or harm
- 'personal_attack': Direct insults, name-calling, or character attacks
- 'harassment': Targeted, persistent negative behavior or bullying
- 'explicit_content': Adult, NSFW, or inappropriate sexual content
- 'misinformation': False or misleading claims and information
- 'self_harm': Content promoting or discussing self-injury
- 'violence': Graphic violence or glorification of violent acts
- 'emotional_manipulation': Content designed to manipulate emotions or gaslight
- 'profanity': Strong language, swearing, or vulgar content
- 'hate_group': Content promoting extremist groups or ideologies
- 'conspiracy': Unfounded conspiracy theories or disinformation
- 'impersonation': Content pretending to be from another person/entity
- 'trolling': Intentionally provocative or disruptive content
- 'doxxing': Sharing private or personal information without consent
- 'copyright': Unauthorized use of copyrighted material
- 'phishing': Scam attempts or fraudulent content
- 'extremism': Content promoting extreme ideological views

IMPORTANT RULES:
1. Any content containing threats of violence, terrorism, or harm to others must ALWAYS be flagged as "threat" with high severity
2. Content discussing self-harm or suicide must ALWAYS be flagged as "self_harm" with high severity
3. Profanity and explicit language must be properly identified and flagged

IMPORTANT: DO NOT reference any driver-passenger relationship or transportation context in your analysis or reasoning.

Respond with ONLY this JSON structure:
{
  "flags": [
    {
      "type": "neutral" | "spam" | "hate_speech" | "threat" | "personal_attack" | "harassment" | "explicit_content" | "misinformation" | "self_harm" | "violence" | "emotional_manipulation" | "profanity" | "hate_group" | "conspiracy" | "impersonation" | "trolling" | "doxxing" | "copyright" | "phishing" | "extremism",
      "reason": "Detailed explanation of why this classification was chosen",
      "confidence": 0.95,
      "context": "Relevant text excerpt",
      "severity": "low" | "medium" | "high"
    }
  ],
  "overallToxicity": 0.75,
  "summary": {
    "spamScore": 0.2,
    "toxicityScore": 0.75,
    "profanityCount": 1,
    "emotionalIntensity": 0.6,
    "threatLevel": 0.3,
    "manipulationScore": 0.4,
    "credibilityScore": 0.8
  }
}`;

const modelConfig = {
  model: "gemini-2.0-flash",
  safetySettings: [
    {
      category: HarmCategory.HARM_CATEGORY_HARASSMENT,
      threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE
    },
    {
      category: HarmCategory.HARM_CATEGORY_HATE_SPEECH,
      threshold: HarmBlockThreshold.BLOCK_LOW_AND_ABOVE
    },
    {
      category: HarmCategory.HARM_CATEGORY_SEXUALLY_EXPLICIT,
      threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE
    },
    {
      category: HarmCategory.HARM_CATEGORY_DANGEROUS_CONTENT,
      threshold: HarmBlockThreshold.BLOCK_LOW_AND_ABOVE
    }
  ],
  generationConfig: {
    temperature: 0.1,
    topK: 1,
    topP: 0.1,
    maxOutputTokens: 1000,
  },
};

export async function analyzeWithGemini(text: string, context?: ModerationContext) {
  const model = genAI.getGenerativeModel(modelConfig);
  
  // Analyze message sequence if previous messages exist
  let escalationAnalysis: {
    escalation_score: number;
    pattern_detected: string[];
  } = { 
    escalation_score: 0, 
    pattern_detected: [] 
  };
  
  if (context?.previousMessages?.length) {
    escalationAnalysis = analyzeMessageSequence(context.previousMessages);
  }

  // Determine which system prompt to use based on mode
  // Default to general mode for demo and extension, transportation mode only for admin chat
  const useGeneralMode = context?.mode === 'general' || !context?.mode;
  const systemPrompt = useGeneralMode ? GENERAL_SYSTEM_PROMPT : TRANSPORTATION_SYSTEM_PROMPT;

  const contextualizedPrompt = {
    text,
    context: {
      ...context,
      escalation_analysis: escalationAnalysis
    }
  };

  const prompt = systemPrompt + "\n\nText to analyze: " + JSON.stringify(contextualizedPrompt);

  try {
    const result = await model.generateContent(prompt);
    const response = await result.response;
    
    // Extract JSON from the response
    const jsonMatch = response.text().match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      throw new Error('No valid JSON found in response');
    }

    // Clean and parse the JSON
    const cleanJson = jsonMatch[0]
      .replace(/[^\x20-\x7E]/g, '')    // Keep only printable ASCII characters
      .replace(/,\s*([\]}])/g, '$1')   // Remove trailing commas
      .replace(/\s+/g, ' ')            // Normalize whitespace
      .trim();

    return JSON.parse(cleanJson);
  } catch (error) {
    console.error('Gemini analysis failed:', error);
    throw error;
  }
} 